import { isTimeAxis } from '../../../types/guards.js';
import type {
  XYChartAreaVariant,
  XYChartAxisInternal,
  XYChartOrientation,
} from '../../../types/xy-chart-internals.js';
import {
  type AreaDatapoint,
  type DotDatapoint,
  type LineDatapoint,
} from '../../../types/xy-chart.js';
import { getCenterDate } from '../../../utils/get-center-date.js';

/**
 * Calculates the linear point for Line and Dot series.
 */
export function getCalculatedLinearPoint(
  datapoint: DotDatapoint | LineDatapoint,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
) {
  let x0 = datapoint.x0 ?? datapoint.x1;
  let y0 = datapoint.y1 ?? datapoint.y0;

  if (isTimeAxis(xAxis)) {
    x0 = getCenterDate(
      datapoint.x0 as Date | undefined,
      datapoint.x1 as Date | undefined,
    );
  }
  if (isTimeAxis(yAxis)) {
    y0 = getCenterDate(
      datapoint.y0 as Date | undefined,
      datapoint.y1 as Date | undefined,
    );
  }

  return { x0, y0 };
}

/**
 * Calculates the linear point for Area series based on orientation and variant.
 * For vertical areas: returns x0 (primary axis) and y1 (stacking endpoint)
 * For horizontal areas: returns y0 (primary axis) and x1 (stacking endpoint)
 * For bands: returns the primary axis centered and both endpoints
 */
export function getCalculatedLinearPointForArea(
  datapoint: AreaDatapoint,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
  chartOrientation?: XYChartOrientation,
  variant?: XYChartAreaVariant,
) {
  if (variant === 'area') {
    if (chartOrientation === 'vertical') {
      let x0 = datapoint.x0 ?? datapoint.x1;
      let y1 = datapoint.y1 ?? datapoint.y0;

      if (isTimeAxis(xAxis)) {
        x0 = getCenterDate(
          datapoint.x0 as Date | undefined,
          datapoint.x1 as Date | undefined,
        );
      }
      if (isTimeAxis(yAxis)) {
        y1 = getCenterDate(
          datapoint.y0 as Date | undefined,
          datapoint.y1 as Date | undefined,
        );
      }

      return { x0, y1 };
    }
    let y0 = datapoint.y0 ?? datapoint.y1;
    let x1 = datapoint.x1 ?? datapoint.x0;

    if (isTimeAxis(yAxis)) {
      y0 = getCenterDate(
        datapoint.y0 as Date | undefined,
        datapoint.y1 as Date | undefined,
      );
    }
    if (isTimeAxis(xAxis)) {
      x1 = getCenterDate(
        datapoint.x0 as Date | undefined,
        datapoint.x1 as Date | undefined,
      );
    }

    return { y0, x1 };
  }

  // band variants
  if (chartOrientation === 'vertical') {
    let x0 = datapoint.x0;
    const y0 = datapoint.y0;
    const y1 = datapoint.y1;

    if (isTimeAxis(xAxis)) {
      x0 = getCenterDate(
        datapoint.x0 as Date | undefined,
        datapoint.x1 as Date | undefined,
      );
    }

    return { x0, y0, y1 };
  }

  if (chartOrientation === 'horizontal') {
    const x0 = datapoint.x0;
    const x1 = datapoint.x1;
    let y0 = datapoint.y0;

    if (isTimeAxis(yAxis)) {
      y0 = getCenterDate(
        datapoint.y0 as Date | undefined,
        datapoint.y1 as Date | undefined,
      );
    }

    return { y0, x0, x1 };
  }
  // full-band
  let x0 = datapoint.x0;
  const y0 = datapoint.y0;
  const y1 = datapoint.y1;

  if (isTimeAxis(xAxis)) {
    x0 = getCenterDate(
      datapoint.x0 as Date | undefined,
      datapoint.x1 as Date | undefined,
    );
  }

  return { x0, y0, y1 };
}
